import { Link } from "react-router-dom";
import { Layers, ArrowUpRight, Lock } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { hasFeature, tierLabel } from "@/lib/tier";

/**
 * InvestorComparisonCTA — calm in-report CTA that deep-links to
 * /investor/comparison and highlights the current property.
 *
 * Behavior:
 *   - Pro+ users: small actionable card linking to Deal Comparison.
 *   - Lite users: same card but with a Lock icon + "Upgrade to Pro" copy.
 *     (This acts as a free in-product growth nudge from Lite → Pro.)
 *   - Anonymous / sample viewers / non-investor buyers: renders nothing.
 *
 * Cost-Safe: pure render, zero API calls.
 */
export default function InvestorComparisonCTA({ reportId, isSample }) {
    const { user } = useAuth();
    if (!user || isSample) return null;
    // Only show to active investors (Lite, Pro, or Plus).
    const tier = user?.tier?.tier;
    if (!tier) return null;

    const isPro = hasFeature(user, "deal_comparison_dashboard");

    return (
        <div
            className="bl-card p-5 md:p-6 mt-6 flex flex-col sm:flex-row sm:items-center gap-4 bg-bl-teal-tint/25"
            data-testid="investor-comparison-cta"
        >
            <div className="w-10 h-10 shrink-0 rounded-lg bg-bl-surface flex items-center justify-center">
                {isPro ? (
                    <Layers className="w-5 h-5 text-bl-primary" />
                ) : (
                    <Lock className="w-5 h-5 text-bl-primary" />
                )}
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-[10px] tracking-[0.2em] uppercase text-bl-primary">
                    Deal comparison
                </div>
                <h3 className="mt-1 font-display font-semibold text-bl text-lg leading-tight">
                    {isPro
                        ? "Compare this property with your other deals."
                        : "Compare deals side-by-side with Investor Pro."}
                </h3>
                <p className="mt-1.5 text-sm text-bl-muted leading-relaxed">
                    {isPro
                        ? "See repair exposure, leverage, pipeline status, and notes across every property — without re-opening each report."
                        : "Open this property's repair exposure, leverage, and pipeline status alongside every other report in one calm view."}
                </p>
            </div>
            {isPro ? (
                <Link
                    to={`/investor/comparison?highlight=${encodeURIComponent(reportId)}`}
                    className="bl-btn-primary text-sm shrink-0"
                    data-testid="investor-comparison-cta-open"
                >
                    Open comparison <ArrowUpRight className="w-4 h-4" />
                </Link>
            ) : (
                <Link
                    to="/pricing"
                    className="bl-btn-secondary text-sm shrink-0"
                    data-testid="investor-comparison-cta-upgrade"
                >
                    Upgrade to {tierLabel("investor_pro")}{" "}
                    <ArrowUpRight className="w-4 h-4" />
                </Link>
            )}
        </div>
    );
}
